import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Phone, Mail, MapPin, Clock, ArrowRight, Loader2 } from "lucide-react";
import { COMPANY, SERVICE_OPTIONS } from "@/data";
import { trackLead } from "@/lib/analytics";
import Reveal from "@/components/landing/Reveal";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CONTACT_ITEMS = [
  { icon: Phone, label: "Call Us", value: COMPANY.phone, href: `tel:${COMPANY.phoneRaw}` },
  { icon: Mail, label: "Email", value: COMPANY.email, href: `mailto:${COMPANY.email}` },
  { icon: MapPin, label: "Service Area", value: COMPANY.serviceArea },
  { icon: Clock, label: "Scheduling", value: "Flexible Scheduling — Call to Book" },
];

export const Contact = () => {
  const [form, setForm] = useState({
    name: "",
    phone: "",
    email: "",
    service_type: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.email || !form.service_type) {
      toast.error("Please fill in your name, phone, email and service type.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/leads`, { ...form, lead_type: "estimate" });
      trackLead();
      toast.success("Request received! We'll reach out shortly to schedule your free estimate.");
      setForm({ name: "", phone: "", email: "", service_type: "", message: "" });
    } catch (err) {
      toast.error("Something went wrong. Please call us at " + COMPANY.phone);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="bg-white py-24 md:py-32">
      <div className="max-w-[1440px] mx-auto px-5 md:px-12">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-start">
          {/* Info */}
          <div>
            <Reveal>
              <span className="font-display uppercase tracking-[0.2em] text-gold text-sm font-semibold">
                07 — Let's Talk
              </span>
              <h2 className="mt-4 font-display font-bold uppercase text-navy text-4xl md:text-6xl leading-[0.9] tracking-tight">
                Request your free estimate
              </h2>
              <p className="mt-5 font-body text-navy/60 text-lg max-w-md">
                Tell us about your project. It's 100% free, with no obligation —
                just honest advice and a clear quote.
              </p>
            </Reveal>

            <div className="mt-10 grid sm:grid-cols-2 gap-5">
              {CONTACT_ITEMS.map((c, i) => {
                const Icon = c.icon;
                const inner = (
                  <div className="flex items-start gap-4 bg-cloud rounded-2xl p-5 h-full hover:bg-navy/5 transition-colors duration-200">
                    <span className="shrink-0 w-11 h-11 rounded-full bg-navy flex items-center justify-center">
                      <Icon size={20} className="text-gold" />
                    </span>
                    <div>
                      <div className="font-body uppercase tracking-wide text-navy/45 text-xs font-bold">
                        {c.label}
                      </div>
                      <div className="mt-1 font-body font-semibold text-navy text-[15px]">
                        {c.value}
                      </div>
                    </div>
                  </div>
                );
                return (
                  <Reveal key={c.label} delay={i * 0.06}>
                    {c.href ? (
                      <a href={c.href} data-testid={`contact-${c.label.toLowerCase().replace(/\s/g, "-")}`}>
                        {inner}
                      </a>
                    ) : (
                      inner
                    )}
                  </Reveal>
                );
              })}
            </div>
          </div>

          {/* Form */}
          <Reveal delay={0.15}>
            <form
              onSubmit={submit}
              data-testid="estimate-form"
              className="bg-cloud rounded-3xl p-7 md:p-10 border border-navy/5 shadow-[0_20px_60px_rgba(15,45,82,0.08)]"
            >
              <div className="space-y-5">
                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <label className="font-body text-sm font-semibold text-navy mb-2 block">Name</label>
                    <Input
                      data-testid="form-name"
                      value={form.name}
                      onChange={(e) => update("name", e.target.value)}
                      placeholder="John Smith"
                      className="bg-white border-navy/10 h-12 rounded-xl focus-visible:ring-gold"
                    />
                  </div>
                  <div>
                    <label className="font-body text-sm font-semibold text-navy mb-2 block">Phone</label>
                    <Input
                      data-testid="form-phone"
                      value={form.phone}
                      onChange={(e) => update("phone", e.target.value)}
                      placeholder="(704) 000-0000"
                      className="bg-white border-navy/10 h-12 rounded-xl focus-visible:ring-gold"
                    />
                  </div>
                </div>

                <div>
                  <label className="font-body text-sm font-semibold text-navy mb-2 block">Email</label>
                  <Input
                    data-testid="form-email"
                    type="email"
                    value={form.email}
                    onChange={(e) => update("email", e.target.value)}
                    placeholder="you@email.com"
                    className="bg-white border-navy/10 h-12 rounded-xl focus-visible:ring-gold"
                  />
                </div>

                <div>
                  <label className="font-body text-sm font-semibold text-navy mb-2 block">Service Type</label>
                  <Select value={form.service_type} onValueChange={(v) => update("service_type", v)}>
                    <SelectTrigger
                      data-testid="form-service"
                      className="bg-white border-navy/10 h-12 rounded-xl focus:ring-gold"
                    >
                      <SelectValue placeholder="Select a service" />
                    </SelectTrigger>
                    <SelectContent>
                      {SERVICE_OPTIONS.map((s) => (
                        <SelectItem key={s} value={s}>
                          {s}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <label className="font-body text-sm font-semibold text-navy mb-2 block">Message</label>
                  <Textarea
                    data-testid="form-message"
                    value={form.message}
                    onChange={(e) => update("message", e.target.value)}
                    placeholder="Tell us about your project..."
                    rows={4}
                    className="bg-white border-navy/10 rounded-xl focus-visible:ring-gold resize-none"
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  data-testid="form-submit-btn"
                  className="group w-full inline-flex items-center justify-center gap-2 rounded-full bg-gold hover:bg-gold-hover disabled:opacity-70 text-navy-dark font-bold uppercase tracking-wide text-sm px-8 py-4 transition-colors duration-200"
                >
                  {loading ? (
                    <>
                      <Loader2 size={18} className="animate-spin" /> Sending...
                    </>
                  ) : (
                    <>
                      Request Free Estimate
                      <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform duration-200" />
                    </>
                  )}
                </button>
              </div>
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

export default Contact;
